import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Globe } from 'lucide-react';
import { formatWalkedDistance } from '../lib/walkingStats';

/**
 * CitiesExploredMap — a compact world-map view of every city the traveller
 * has walked in. Renders a circle marker per city; radius scales with total
 * walked distance so a well-explored city stands out from a one-tour city.
 *
 * Positioning + zoom are chosen from the bounding box of the traveller's
 * pins (fits every city with a bit of padding). We deliberately DON'T use
 * regular Marker pins so we can size each city visually — a subtle but
 * satisfying "collector" cue.
 *
 * Props:
 *   cities — [{ city, distance_meters, duration_seconds, completions, lat, lng }, ...]
 *            Rows without lat/lng are skipped (they can't be plotted).
 */
const FitBounds = ({ points }) => {
  // Uses the leaflet map instance to fit all city pins into view on mount
  // and whenever the points array changes.
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 10);
      return;
    }
    const bounds = points.map((p) => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [points, map]);
  return null;
};

const CitiesExploredMap = ({ cities }) => {
  // Only pins that actually have coordinates — filter here so <MapContainer>
  // is only mounted when we have at least one plottable point.
  const points = useMemo(
    () => (cities || []).filter((c) => c && c.lat != null && c.lng != null),
    [cities]
  );

  // Radius scale: log-ish curve keeps huge walks from dwarfing tiny ones.
  // 5 km → r=8, 20 km → r=12, 100 km → r=18, 500 km → r=24. Clamped 6..28.
  const radiusFor = (meters) => {
    const km = Math.max(0.1, (meters || 0) / 1000);
    const r = 6 + Math.log2(km + 1) * 3.5;
    return Math.min(28, Math.max(6, r));
  };

  if (points.length === 0) return null;

  return (
    <div
      className="max-w-5xl mx-auto px-6 pt-6"
      data-testid="cities-explored-map-section"
    >
      <div className="bg-white rounded-2xl shadow-md border border-amber-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-amber-100 flex items-center gap-2">
          <Globe className="w-5 h-5 text-amber-700" />
          <h2 className="font-black text-slate-900 text-lg">Cities explored</h2>
          <span className="text-xs text-slate-500 ml-auto">
            {points.length} on the map · pin size = distance walked
          </span>
        </div>
        <div className="relative" style={{ height: '360px' }}>
          <MapContainer
            center={[points[0].lat, points[0].lng]}
            zoom={4}
            scrollWheelZoom={false}
            className="h-full w-full"
            data-testid="cities-explored-map"
            style={{ background: '#fef7e6' }}
          >
            {/* Watercolor-ish neutral basemap keeps the passport aesthetic
                warm without competing with the amber pins. */}
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {points.map((c) => {
              const uniqueTours = c.unique_tours || 0;
              // Numeric "N tours" badge overlaid on the city pin — only shows
              // when the traveller has completed 2+ distinct tours here.
              // Without this, "3 tours in Tel Aviv" and "same tour 3 times"
              // look identical on the map. Rendered as a tiny divIcon Marker
              // anchored to the same coord as the CircleMarker.
              const badgeIcon =
                uniqueTours > 1
                  ? L.divIcon({
                      className: 'cities-explored-badge',
                      html: `<div style="
                        background:#b45309;
                        color:#fff7ed;
                        border:2px solid #fef3c7;
                        border-radius:9999px;
                        min-width:22px;
                        height:22px;
                        padding:0 6px;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        font-size:11px;
                        font-weight:900;
                        line-height:1;
                        box-shadow:0 2px 6px rgba(0,0,0,0.25);
                        font-family:system-ui,-apple-system,sans-serif;
                      " data-testid="city-tour-count-badge-${c.city}">${uniqueTours}</div>`,
                      iconSize: [22, 22],
                      iconAnchor: [-8, 24],
                    })
                  : null;
              return (
                <React.Fragment key={c.city}>
                  <CircleMarker
                    center={[c.lat, c.lng]}
                    radius={radiusFor(c.distance_meters)}
                    pathOptions={{
                      color: '#b45309',      // amber-700 stroke
                      weight: 2,
                      fillColor: '#f59e0b',  // amber-500 fill
                      fillOpacity: 0.55,
                    }}
                  >
                    <Tooltip
                      direction="top"
                      offset={[0, -6]}
                      opacity={1}
                      permanent={points.length <= 3}
                    >
                      <div className="text-xs">
                        <div className="font-black text-slate-900">{c.city}</div>
                        <div className="text-slate-600">
                          {formatWalkedDistance(c.distance_meters || 0)}
                          {uniqueTours > 0 && ` · ${uniqueTours} ${uniqueTours === 1 ? 'tour' : 'tours'}`}
                        </div>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                  {badgeIcon && (
                    <Marker
                      position={[c.lat, c.lng]}
                      icon={badgeIcon}
                      interactive={false}
                      keyboard={false}
                    />
                  )}
                </React.Fragment>
              );
            })}
            <FitBounds points={points} />
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default CitiesExploredMap;
